import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH, unlimitedMaxBenefitMagicNumber } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  growthFactor,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { applyIndemnitySurplus } from '../sharedSurplus';

// ---------------------------------------------------------------------------
// Short-Term Care Insurance (STCI) — generic reimbursement product.
//
// STCI IS traditional/standalone LTC PLUS a hard benefit-period (day) cap.
// This file mirrors `traditionalLtcStrategy.ts` near line-for-line; the ONLY
// substantive difference is the limited benefit period:
//
// BENEFIT-PERIOD (DAY) CAP — the defining trait:
// - Total *paid* days are capped at `policyBenefitPeriodMonths × 30` (e.g. a
//   12-month benefit period → 360 payable days). Once those days are consumed,
//   the policy pays $0 even if the dollar pool still has money in it.
// - The cap is applied to ELIGIBLE (post-2-ADL, post-WP) "paying" days, BEFORE
//   the dollar-pool draw. Waiting-period days and <2-ADL days do NOT consume
//   the benefit period — only days the policy actually pays for. This mirrors
//   legacy `handlePhaseBenefitEligibleCareDays`
//   (`calcFundingSourceDerivatives.ts:837,855-865`): legacy clamps eligible
//   days to `policyBenefitPeriodMonthsRemaining × 30` and decrements that
//   counter by the clamp result.
// - The remaining benefit-period days live in `strategyState`
//   (`benefitPeriodDaysRemaining`), not on the policy — strategies must not
//   mutate `ltcPolicy`. Stored in days (not months) to avoid round-tripping
//   ×30 / ÷30; they reconcile to the legacy months arithmetic exactly because
//   30 is the shared `DAYS_IN_MONTH`.
//
// LEGACY QUIRK (Q2 — intentionally preserved for parity): a row where the
// dollar pool is already exhausted ($0 paid) STILL consumes benefit-period
// days, because legacy decrements by the day-clamp result independently of the
// dollar draw. We decrement `benefitPeriodDaysRemaining` by `payableDays`
// regardless of whether the pool also bound. See the test
// "dollar-pool-exhausted rows still consume benefit-period days".
//
// SHARP EDGE (null benefit period): if `policyBenefitPeriodMonths` (and its
// Remaining variant) is null/0, `benefitPeriodDaysRemaining` seeds to 0 and
// the policy SILENTLY PAYS $0 for the entire claim. This matches legacy's
// `?? 0` at `:837`. A misconfigured STCI policy with no benefit period pays
// nothing — there is no "unlimited period" fallback by design (an unlimited
// period would just be traditional LTC). See the loud null-period test.
//
// Everything else (single pool from `policyMaximumBenefitAmount`, no EOB pool,
// no death benefit, reimbursement-or-indemnity payout, 2-ADL gate, two-tier
// waiting period with no retroactive lump, at-issue-anchored daily-cap
// inflation, remaining-balance pool inflation) is identical to
// `traditionalLtcStrategy`. STCI is single-life only — there is no joint def.
// ---------------------------------------------------------------------------

export interface ShortTermCareState {
  // Remaining payable days in the limited benefit period, in DAYS. Seeded in
  // `initClaimState` from `policyBenefitPeriodMonths × DAYS_IN_MONTH` (or the
  // Remaining variant on a resumed claim). Decremented each month by the
  // eligible (paying) days that draw from the pool; clamps at 0. Mirrors
  // legacy `policyBenefitPeriodMonthsRemaining × 30`.
  benefitPeriodDaysRemaining: number;
  // Indemnity-surplus reserve. Indemnity policies pay a fixed monthly benefit
  // regardless of cost; the excess above eligible cost is banked here and
  // drained on later shortfall months (legacy `applyIndemnityPayoutToPhase`
  // parity). Always 0 on reimbursement-election policies (their payout never
  // exceeds eligible cost), so the surplus model is a numeric no-op there.
  indemnitySurplus: number;
}

export interface ShortTermCareDeltas {
  // Eligible (paying) days that consumed the benefit period this row — the
  // amount `benefitPeriodDaysRemaining` dropped by. 0 on WP / <2-ADL /
  // period-exhausted rows; NON-zero even on a dollar-pool-exhausted row that
  // still has eligible days (the legacy Q2 quirk). Equals the per-row analogue
  // of legacy `phaseEligibleCareDays`. Surfaced as a delta so the transcript /
  // UI can show "X benefit-period days used this month" without back-deriving
  // from two state snapshots.
  benefitPeriodDaysConsumed: number;
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
}

interface InflationConfig {
  rate: number;
  capYears: number;
  isSimple: boolean;
}

function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    rate: ltcPolicy.policyInflationProtection ?? 0,
    capYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : 0,
    isSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

// A standalone STCI policy with a null/undefined `policyMaximumBenefitAmount`
// is UNLIMITED, not $0 — matching legacy `isUnlimitedBenefit` (a missing max on
// a standard policy returns `unlimitedMaxBenefitMagicNumber` and flags
// `policyBenefitIsUnlimited`). The finite benefit-period (day) cap still bounds
// payouts; the dollar pool simply isn't the binding constraint. A populated max
// (including 0) is a real cap.
function isUnlimitedPool(ltcPolicy: PolicyFundingSource): boolean {
  return ltcPolicy.policyMaximumBenefitAmount == null;
}

// At-issue lifetime dollar pool. Unlimited policies seed the sentinel so the
// dollar pool never binds (the benefit-period day cap still applies); the
// client renders any pool >= the sentinel as "Unlimited". A populated max
// (including 0) is used verbatim.
function getAtIssuePool(ltcPolicy: PolicyFundingSource): number {
  if (isUnlimitedPool(ltcPolicy)) return unlimitedMaxBenefitMagicNumber;
  return ltcPolicy.policyMaximumBenefitAmount ?? 0;
}

// Seed for the benefit-period day counter. Prefer the on-claim Remaining
// value (resumed claim) and fall back to the at-issue `policyBenefitPeriodMonths`.
// Null/0 → 0 days, which clamps all payouts to $0 (see SHARP EDGE above). The
// new engine resets Remaining via `initializeRemainingFields`, so for a fresh
// projection both fields agree.
function getSeedBenefitPeriodDays(ltcPolicy: PolicyFundingSource): number {
  const months =
    ltcPolicy.policyBenefitPeriodMonthsRemaining ??
    ltcPolicy.policyBenefitPeriodMonths ??
    0;
  return months * DAYS_IN_MONTH;
}

export const shortTermCareStrategy: PolicyProjectionStrategy<
  ShortTermCareState,
  ShortTermCareDeltas
> = {
  initClaimState(client, ltcPolicy): MonthlyState<ShortTermCareState> {
    const atIssuePool = getAtIssuePool(ltcPolicy);
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const inflation = readInflation(ltcPolicy);
    // Anniversaries between policy issue and claim start are not iterated, so
    // pre-inflate the pool to its value at claim start. Claims starting in
    // policy year 1 keep the factor at 1.0. The benefit period does NOT
    // pre-decrement for pre-claim years — it only consumes on paid claim days.
    // Unlimited pools stay flat at the sentinel — no pre-claim inflation, no
    // utilized-to-date reduction (mirrors traditionalLtcStrategy; the finite
    // benefit-period day cap remains the binding constraint).
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const poolAtClaimStart = isUnlimitedPool(ltcPolicy)
      ? atIssuePool
      : atIssuePool *
        growthFactor(inflation.rate, inflation.capYears, inflation.isSimple, claimStartPolicyYear);
    return {
      pools: {
        baseBenefitPool: isUnlimitedPool(ltcPolicy)
          ? atIssuePool
          : Math.max(poolAtClaimStart - utilizedToDate, 0),
        // STCI has no continuation-of-benefits pool and no death benefit.
        extensionOfBenefitsPool: 0,
        deathBenefit: 0,
      },
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
          ltcPolicy.homeCareWaitingPeriodDays ??
          0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: {
        benefitPeriodDaysRemaining: getSeedBenefitPeriodDays(ltcPolicy),
        indemnitySurplus: 0,
      },
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const inflation = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // Prior utilization deducted from the single dollar pool at claim start,
    // capped at the pre-inflated pool (mirrors `initClaimState`'s
    // `Math.max(poolAtClaimStart − utilized, 0)`). The benefit-period day cap is
    // independent of utilization. Unlimited (null-max) pools deduct nothing —
    // the sentinel pool is seeded verbatim, never reduced by utilization.
    const claimStartPolicyYear = getCalendarPolicyYear(clientAsClient, 0);
    const poolAtClaimStart =
      getAtIssuePool(ltcPolicy) *
      growthFactor(inflation.rate, inflation.capYears, inflation.isSimple, claimStartPolicyYear);
    const priorUtilization = isUnlimitedPool(ltcPolicy)
      ? 0
      : Math.min(ltcPolicy.policyBenefitUtilizedToDate ?? 0, poolAtClaimStart);
    return {
      strategyId: 'short-term-care',
      priorUtilization,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Same sparse-field fallback the payout cap uses, so the labelled
      // per-environment benefits always match what the engine actually pays.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.home,
        ),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // Single product-wide inflation rate drives both the daily-cap step and
      // the pool growth. No separate face/COB rate — leave the EOB-pool knobs
      // undefined (no second pool).
      inflationRate: inflation.rate,
      inflationCapYears: inflation.capYears,
      inflationIsSimple: inflation.isSimple,
      baseBenefitPoolInflationRate: inflation.rate,
      baseBenefitPoolInflationCapYears: inflation.capYears,
      baseBenefitPoolAtIssue: getAtIssuePool(ltcPolicy),
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      // No death benefit on STCI.
      minimumGuaranteedDeathBenefit: 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
      // A null-max STCI policy is UNLIMITED (sentinel pool, rendered as
      // "Unlimited" by the client) — the finite benefit-period day cap is then
      // the binding constraint, not the dollar pool (P2-20).
      isUnlimited: isUnlimitedPool(ltcPolicy),
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    // Verbatim copy of traditional LTC: the benefit period caps total *days*
    // (a lifetime constraint), not the per-year daily cap or pool ceiling, so
    // the year-snapshot rates are unchanged by it.
    const { ltcPolicy, policyYear } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const inflation = readInflation(ltcPolicy);
    const factor = growthFactor(
      inflation.rate,
      inflation.capYears,
      inflation.isSimple,
      policyYear,
    );

    const atIssuePool = getAtIssuePool(ltcPolicy);
    const inflatedMonthlyCap = baseMonthly * factor;
    // Unlimited pools stay flat at the sentinel (the client renders >= sentinel
    // as "Unlimited"); only the daily/monthly cap inflates.
    const inflatedPool = isUnlimitedPool(ltcPolicy) ? atIssuePool : atIssuePool * factor;

    return {
      policyYear,
      effectiveDailyBenefit: inflatedMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: 0,
      // No death benefit / continuation pool on STCI.
      effectiveFaceAmount: 0,
      effectiveContinuationBenefit: 0,
      effectiveTotalLtcBenefit: inflatedPool,
      monthlyBenefitCap: inflatedMonthlyCap,
      theoreticalLtcPoolCeiling: inflatedPool,
    };
  },

  computeMonth(
    input: MonthComputeInput<ShortTermCareState, ShortTermCareDeltas>,
  ): MonthlyEntry<ShortTermCareState, ShortTermCareDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    // Pre-policy gate. The benefit period is not consumed before the policy
    // exists, so pass `openState` through unchanged and emit a zero delta.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<ShortTermCareState, ShortTermCareDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: {
          benefitPeriodDaysConsumed: 0,
          indemnitySurplusAccrued: 0,
          indemnitySurplusApplied: 0,
        },
      });
    }

    // 1. Day classification (2-ADL bucket → WP → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-month dollar cap (this year's inflation factor).
    const inflation = readInflation(ltcPolicy);

    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const monthlyCap =
      baseMonthly *
      growthFactor(inflation.rate, inflation.capYears, inflation.isSimple, policyYear);

    // 3. Annual anniversary pool accrual on the REMAINING balance. Gated on
    //    "pool still > 0", "benefit period not yet exhausted", and within cap
    //    years. Skipped on the unlimited sentinel pool — the sentinel must not
    //    accrue (it would add tens of millions per anniversary; P2-20).
    //
    //    The benefit-period guard is the ONE divergence from traditional LTC:
    //    STCI is the only strategy where payouts can cease (day cap exhausted)
    //    while the dollar pool still holds money. Once `benefitPeriodDaysRemaining`
    //    hits 0 the policy can never pay another dollar, so the remaining pool —
    //    and the "Pool remaining" column that reads it — must FREEZE rather than
    //    keep compounding (issue #4). Freezing (not zeroing) preserves the
    //    per-pool identity (close = open + accrual − depletion) and keeps the
    //    undrawn residual OUT of any non-LTC payout / death-benefit total, which
    //    read the death-benefit pool (always 0 for STCI), never this pool.
    let inflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      // A capYears-year rider applies capYears increases, the last one landing
      // on the anniversary entering policy year capYears + 1 — matching
      // `growthFactor`'s min(policyYear − 1, capYears) step convention, so the
      // in-claim pool agrees with `buildYearSnapshot` through the final cap year.
      const withinCap =
        inflation.capYears <= 0 || policyYear <= inflation.capYears + 1;
      if (
        !isUnlimitedPool(ltcPolicy) &&
        openState.pools.baseBenefitPool > 0 &&
        openState.strategyState.benefitPeriodDaysRemaining > 0 &&
        withinCap &&
        inflation.rate > 0
      ) {
        inflationAccrual = inflation.isSimple
          ? getAtIssuePool(ltcPolicy) * inflation.rate
          : openState.pools.baseBenefitPool * inflation.rate;
      }
    }
    const poolAfterAccrual = openState.pools.baseBenefitPool + inflationAccrual;

    // 4. NEW (STCI) — benefit-period cap on eligible days. Clamp the *paying*
    //    days to the remaining benefit period BEFORE the dollar draw. Mirrors
    //    legacy `phaseEligibleCareDays = min(eligibleDaysAfterGates,
    //    policyBenefitPeriodDaysRemaining)` (`:856-859`). Days that are
    //    ADL/WP-eligible but stranded by an exhausted period
    //    (`eligibleDays - payableDays`) are fully out-of-pocket.
    const periodDaysRemaining = openState.strategyState.benefitPeriodDaysRemaining;
    const payableDays = Math.min(eligibleDays, periodDaysRemaining);

    // 5. Payout using `payableDays`: reimbursement (default) or cash indemnity.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    const proRatedCap = monthlyCap * (payableDays / DAYS_IN_MONTH);
    // Two distinct cost bases (they differ ONLY when the benefit period binds,
    // i.e. payableDays < eligibleDays):
    //  • `payableEligibleCost` — cost of the period-COVERED days; bounds the
    //    reimbursement payout (the policy only reimburses days within the period).
    //  • `eligibleCost` — cost of ALL ADL/WP-eligible days, NOT clamped by the
    //    benefit period; the surplus reserve's cost basis. Un-clamped so the
    //    BOUNDARY row that exhausts the period mid-month (payableDays < eligibleDays,
    //    but still a partial rowPayout > 0) reports coverage against the full
    //    month's cost and drains its out-of-pocket remainder. On FULLY
    //    period-exhausted rows `applyIndemnitySurplus` freezes the reserve
    //    (the call passes `policyExhausted` from the real pool/period state) —
    //    banked surplus must NOT bleed into post-exhaustion OOP. Surplus draws
    //    cover cost only; they never consume benefit-period days or the dollar pool.
    const payableEligibleCost = costMetrics.dailyCareCost * payableDays;
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;

    let drawFromPool = 0;
    let indemnityPaid = 0;
    if (isIndemnity && poolAfterAccrual > 0 && payableDays > 0) {
      const indemnityAmount = Math.min(proRatedCap, poolAfterAccrual);
      drawFromPool = indemnityAmount;
      indemnityPaid = indemnityAmount;
    } else {
      const wouldPay = Math.min(payableEligibleCost, proRatedCap);
      drawFromPool = Math.min(wouldPay, poolAfterAccrual);
    }
    // Gross cash this row pays out. On indemnity this is the capped cash
    // regardless of cost; on reimbursement it is the reimbursed amount
    // (≤ eligible cost on the payable days). The pool depletes by this gross
    // cash; the surplus model is a separate accounting reserve on top.
    const rowPayout = drawFromPool;

    // 5b. Indemnity-surplus accounting (per-row, unconditional). Cost-coverage is
    //     capped at the (un-period-clamped) eligible cost; indemnity cash above it
    //     banks as surplus and drains on later shortfall rows that still PAY a
    //     benefit (above-cap deficits, and the partial boundary row). Once the
    //     pool OR benefit period is exhausted `applyIndemnitySurplus` FREEZES the
    //     reserve (the call passes `policyExhausted` from real pool/period state)
    //     — the banked surplus carries forward unchanged instead of bleeding into
    //     post-exhaustion out-of-pocket cost. On reimbursement-election policies
    //     the open surplus is always 0 and the reimbursed payout never exceeds
    //     eligible cost, so this stays a numeric no-op. `eligibleCost` is finite
    //     (`dailyCareCost × eligibleDays`), so the sentinel/unlimited-pool quirks
    //     never feed a NaN/Infinity into the surplus call.
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // STC is spent when EITHER the dollar pool OR the benefit period is gone.
      // The partial boundary row opens with period left, so it still drains;
      // fully period-exhausted rows ($0 payout) freeze.
      policyExhausted: poolAfterAccrual <= 0 || periodDaysRemaining <= 0,
    });

    // 6. Benefit-period consumption. Decrement by `payableDays` (the day-clamp
    //    result) regardless of whether the dollar pool also bound — matching
    //    legacy, which decrements by the clamp before applying the pool draw
    //    (Q2 quirk: a $0-paying, pool-exhausted row still burns period days).
    const benefitPeriodDaysConsumed = payableDays;
    const closePeriodDays = Math.max(periodDaysRemaining - payableDays, 0);

    // 7. Closing balance. No death benefit to deplete.
    const closePool = poolAfterAccrual - drawFromPool;

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      // Report the period-clamped `payableDays` (Q3), matching legacy's
      // clamped `phaseEligibleCareDaysAfterWaitingPeriod` (`:862-865`). Days
      // stranded by the exhausted period count as out-of-pocket, not eligible.
      coverageEligibleDays: payableDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: monthlyCap,
      appliedCapPoolSource: 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      // Absorbs both above-cap cost and period-stranded days as out-of-pocket.
      ineligibleCost: costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<ShortTermCareDeltas> = {
      payout,
      base: { depletion: drawFromPool, inflationAccrual },
      eob: { depletion: 0, inflationAccrual: 0 },
      // No death benefit on STCI.
      deathBenefit: { reduction: 0, flooredAtMinimum: false },
      strategyDeltas: {
        benefitPeriodDaysConsumed,
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
      },
    };

    const closeState: MonthlyState<ShortTermCareState> = {
      pools: {
        baseBenefitPool: closePool,
        extensionOfBenefitsPool: 0,
        deathBenefit: 0,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: {
        benefitPeriodDaysRemaining: closePeriodDays,
        indemnitySurplus: surplusResult.closeSurplus,
      },
    };

    // Keep `ltcPoolExhausted` tied to the dollar pool only (Q4). Benefit-period
    // exhaustion is surfaced via a milestone below, not a status flag.
    const status: MonthlyStatus = {
      ltcPoolExhausted: closePool <= 0,
      clientPoolExhausted: closePool <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<ShortTermCareState, ShortTermCareDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: 0,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: inflation.rate > 0,
    });

    // STCI-specific: benefit period exhausted on this row. Reuse the existing
    // `'pool-exhausted'` milestone kind with a distinct label (Q5) — we do not
    // add a new ProjectionMilestoneKind. Fires the month the counter crosses 0.
    // Guarded on `closePool > 0` so a month exhausting BOTH constraints emits
    // only the standard dollar-pool milestone — dollar exhaustion is the
    // dominant event when the two run out together (P2-26).
    if (periodDaysRemaining > 0 && closePeriodDays === 0 && closePool > 0) {
      milestones.push({ kind: 'pool-exhausted', label: 'Benefit period exhausted' });
    }

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };
    // Q6: a row with eligible-but-unpaid days stranded by an exhausted benefit
    // period classifies as `null` (no ineligibility reason) — it is neither
    // <2-ADL, in-WP, nor IL. This matches a dollar-pool-exhausted row in
    // traditional LTC, which also returns null and surfaces exhaustion via the
    // status flag / milestone rather than an ineligibility reason. The existing
    // enum is adequate; no extension needed.
    const ineligibilityReason =
      deriveIneligibilityReason<ShortTermCareState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<ShortTermCareState>(ineligibilityInput);

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  // STCI has no death benefit — always 0.
  finalizeDeathBenefit(_input: FinalizeDeathBenefitInput<ShortTermCareState>): number {
    return 0;
  },
};
